import React, { useState } from "react";
import { useTable, List, EditButton, DeleteButton, ShowButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Button, Input, Card, Row, Col } from "antd";
import { SearchOutlined, AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { PROCUREMENT_PLAN_DELETE_MUTATION, PROCUREMENT_PLAN_LIST_QUERY } from "./queries";
import { useList } from "@refinedev/core";
import { TEAM_LIST_QUERY } from "../teams/queries";
import { UserInfo } from "../../components/UserInfo";

interface IProcurementPlan {
  id: string;
  planNumber: string;
  title: string;
  createdBy: string;
  teamId: string;
  fiscalYear: number;
  planningPeriod: string;
  totalAmount: number;
  submissionDeadline: string;
  submissionDate: string;
  status: string;
}

// Status badges for procurement plans
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusColors: Record<string, string> = {
    DRAFT: 'default',
    SUBMITTED: 'processing',
    UNDER_REVIEW: 'warning',
    APPROVED: 'success',
    REJECTED: 'error',
    PARTIALLY_APPROVED: 'purple',
    EXPIRED: 'red'
  };

  const statusText: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    UNDER_REVIEW: 'Under Review',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    PARTIALLY_APPROVED: 'Partially Approved',
    EXPIRED: 'Expired'
  };

  return (
    <Tag color={statusColors[status] || 'default'}>
      {statusText[status] || status}
    </Tag>
  );
};

export const ProcurementPlanList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Fetch teams for mapping teamId to team name
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  console.log("Teams Data:", teamsData, "Loading:", teamsLoading, "Error:", teamsError);
  // Extract teams from GraphQL response structure
  const teams = Array.isArray(teamsData?.data) ? teamsData?.data : teamsData?.data?.teams?.nodes || [];
   
  
  const { tableProps } = useTable<IProcurementPlan>({
    resource: "procurementPlans",
    initialSorter: [{ field: "createdAt", order: "desc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: PROCUREMENT_PLAN_LIST_QUERY,
    },
    queryOptions: {
      retry(failureCount, error) {
        console.error("Error fetching procurement plans:", error);
        if (error?.message.includes("Network Error") && failureCount <= 3)
          return true;
        return false;
      },
    },
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // setSearch([
    //   {
    //     field: "title",
    //     operator: "contains",
    //     value: value,
    //   },
    // ]);
  };

  const dataSource: IProcurementPlan[] = (tableProps as any)?.dataSource || [];

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Button.Group>
              <Button
                icon={<UnorderedListOutlined />}
                type={viewMode === 'list' ? 'primary' : 'default'}
                onClick={() => setViewMode('list')}
              />
              <Button
                icon={<AppstoreOutlined />}
                type={viewMode === 'grid' ? 'primary' : 'default'}
                onClick={() => setViewMode('grid')}
              />
            </Button.Group>

            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onPressEnter={(e) => handleSearch(e.currentTarget.value)}
              style={{ width: 240 }}
            />
            <CreateButton />
          </Space>
        ),
      }}
    >
      {viewMode === 'list' ? (
        <Table {...tableProps} rowKey="id">
          <Table.Column
            dataIndex="planNumber"
            title="Plan Number"
            sorter
          />
          <Table.Column
            dataIndex="title"
            title="Title"
            sorter
          />
          <Table.Column
            dataIndex="createdBy"
            title="Created By"
            sorter
            render={(userId: string) => <UserInfo userId={userId} />}
          />
          <Table.Column
            dataIndex="teamId"
            title="Team"
            sorter
            render={(teamId: string) => {
            
              if (teamsLoading) return "Loading...";
              if (teamsError) return "Error";
              const team = (teams as any[]).find((t) => t.id === teamId);
              return team ? team.name : "Unknown";
            }}
          />
          <Table.Column
            dataIndex="fiscalYear"
            title="Fiscal Year"
            sorter
          />
          <Table.Column
            dataIndex="planningPeriod"
            title="Period"
            sorter
          />
          <Table.Column
            dataIndex="totalAmount"
            title="Total Amount"
            sorter
            render={(value: number) => `ETB ${value.toLocaleString()}`}
          />
          <Table.Column
            dataIndex="submissionDate"
            title="Submission Date"
            sorter
            render={(date: string) => date ? new Date(date).toLocaleDateString() : '-'}
          />
          <Table.Column
            dataIndex="status"
            title="Status"
            render={(status: string) => <StatusBadge status={status} />}
            sorter
          />
          <Table.Column
            title="Actions"
            dataIndex="actions"
            render={(_text: string, record: IProcurementPlan) => (
              <Space>
                {record.status === 'DRAFT' && (
                  <>
                    <EditButton size="small" recordItemId={record.id} />
                    <DeleteButton size="small" recordItemId={record.id} meta={{ gqlMutation: PROCUREMENT_PLAN_DELETE_MUTATION }} />
                  </>
                )}
                <ShowButton size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        </Table>
      ) : (
        <Row gutter={[16, 16]}>
          {dataSource.map((item) => {
            const team = (teams as any[]).find((t) => t.id === item.teamId);
            return (
              <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  size="small"
                  title={`${item.planNumber} • ${item.title}`}
                  extra={<StatusBadge status={item.status} />}
                >
                  <div style={{ marginBottom: 8 }}>
                    <strong>Team: </strong>{teamsLoading ? 'Loading...' : teamsError ? 'Error' : team ? team.name : 'Unknown'}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Created By: </strong><UserInfo userId={item.createdBy} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Fiscal Year: </strong>{item.fiscalYear}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Period: </strong>{item.planningPeriod}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Total: </strong>{`ETB ${item.totalAmount?.toLocaleString?.() ?? item.totalAmount}`}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Submission Date: </strong>{item.submissionDate ? new Date(item.submissionDate).toLocaleDateString() : '-'}
                  </div>
                  <Space>
                    {item.status === 'DRAFT' && (
                      <>
                        <EditButton size="small" recordItemId={item.id} />
                        <DeleteButton size="small" recordItemId={item.id} meta={{ gqlMutation: PROCUREMENT_PLAN_DELETE_MUTATION }} />
                      </>
                    )}
                    <ShowButton size="small" recordItemId={item.id} />
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </List>
  );
};